"use client";

import {
    apiGetGuessCommentsIndex,
    apiGetGuessCommentsPage,
    apiLikeGuessComment,
    apiListUserBoughtGuessOptions,
    apiUnlikeGuessComment,
    type GuessCommentsIndexItem,
    type GuessCommentsPageItem,
} from "@/api/comments";
import type { GuessCommentsWithoutTitleItem, UserBoughtEventGuessItem } from "@/api/comments";
import defaultAvatar from "@/assets/global/default-avatar.webp";
import { openLoginModal } from "@/contexts/login-modal-context";
import { isLogin } from "@/store/auth";
import { buildCommentUserMap, enrichCommentsWithReplyMentions } from "@/utils/comment-reply";
import { get } from "@/utils/utils";
import { type InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommentsStore } from "./store";
import { CommentData, PositionType } from "./types";

const DEFAULT_AVATAR = defaultAvatar.src;
const COMMENTS_PAGE_SIZE = 8;
const CHILD_PAGE_SIZE = 3;
const LOAD_MORE_SIZE = 8;

const getRootCommentsQueryKey = ( eventNo: string, commentId: string ) => {
    return [ "rootComments", eventNo, commentId ] as const;
};

export const formatCommentTimestamp = ( input?: string | number | Date | null ): string => {
    if ( input === undefined || input === null ) {
        return "";
    }
    const parsed = dayjs( input );
    if ( !parsed.isValid() ) {
        return typeof input === "string" ? input : "";
    }

    const now = dayjs();
    const diffMinutes = now.diff( parsed, "minute" );
    const diffHours = now.diff( parsed, "hour" );
    const diffDays = now.diff( parsed, "day" );

    // Show relative time if less than 3 days
    if ( diffMinutes < 1 ) {
        return "Just now";
    }
    if ( diffMinutes < 60 ) {
        return `${diffMinutes}m`;
    }
    if ( diffHours < 24 ) {
        return `${diffHours}h`;
    }
    if ( diffDays < 3 ) {
        return `${diffDays}d`;
    }

    // Show original format if 3 days or more
    const sameYear = parsed.year() === now.year();
    return parsed.format( sameYear ? "MMM D" : "MMM D, YYYY" );
};

const mapBetTypeToPosition = ( betType?: number ): PositionType => {
    if ( betType === 1 ) return "Yes";
    if ( betType === 2 ) return "No";
    return "No Position";
};

const toCommentData = (
    item: GuessCommentsWithoutTitleItem,
    meta?: {
        rootId?: number;
        parentId?: number;
        eventNo?: string;
        eventTitle?: string;
        guessNo?: string;
        guessTitle?: string;
        categoryName?: string;
        groupTitle?: string;
        userChancePercentage?: number;
        nowChancePercentage?: number;
    },
): CommentData => {
    const serverId = item.id;
    const rootId = meta?.rootId ?? serverId;
    return {
        ...item,
        id: `comment-${serverId}`,
        serverId,
        parentServerId: meta?.parentId,
        rootServerId: rootId,
        userId: item.userId,
        userName: item.username,
        userAvatar: item.userAvatar || DEFAULT_AVATAR,
        time: formatCommentTimestamp( item.createTime ),
        position: mapBetTypeToPosition( item.betType ),
        content: item.content,
        likes: item.likes ?? 0,
        replyToUserName: undefined,
        replies: [],
        highlight: Boolean( item.highlight ),
        betType: item.betType,
        guessNo: meta?.guessNo,
        guessTitle: meta?.guessTitle,
        eventNo: meta?.eventNo,
        eventTitle: meta?.eventTitle,
        likeTag: item.likeTag ?? false,
        comments: item.comments ?? 0,
        categoryName: item.categoryName ?? meta?.categoryName,
        groupTitle: item.groupTitle ?? meta?.groupTitle,
        userChancePercentage: meta?.userChancePercentage,
        nowChancePercentage: meta?.nowChancePercentage,
    };
};

const mapIndexItem = ( item: GuessCommentsIndexItem ): CommentData => {
    const root = toCommentData( item, {
        rootId: item.id,
        eventNo: item.eventNo,
        eventTitle: item.eventTitle,
        guessNo: item.guessNo,
        guessTitle: item.guessTitle,
        categoryName: item.categoryName,
        groupTitle: item.groupTitle,
    } );
    return {
        ...root,
        replies: ( item.children ?? [] ).map( ( child ) =>
            toCommentData( child, {
                rootId: root.serverId,
                parentId: root.serverId,
                eventNo: root.eventNo,
                eventTitle: root.eventTitle,
                guessNo: root.guessNo,
                guessTitle: root.guessTitle,
                categoryName: root.categoryName,
                groupTitle: root.groupTitle,
            } )
        ),
    };
};

const mapPageRootItem = ( item: GuessCommentsPageItem ): CommentData => {
    return toCommentData( item, {
        rootId: item.id,
        eventNo: item.eventNo,
        eventTitle: item.eventTitle,
        guessNo: item.guessNo,
        guessTitle: item.guessTitle,
        categoryName: item.categoryName,
        groupTitle: item.groupTitle,
    } );
};

type RootCommentsPage = {
    records: CommentData[];
    nextOffsetId: number | undefined;
    replyPagination: Record<number, { offsetId: number | null; hasMore: boolean }>;
};

export const useRootCommentsQuery = ( eventNo: string, enabled: boolean = true, commentId?: string ) => {
    const normalizedCommentId = commentId?.trim() ? commentId.trim() : "";
    const parsedCommentId = Number( normalizedCommentId );
    const shouldAttachCommentId = Boolean( normalizedCommentId )
        && Number.isFinite( parsedCommentId )
        && parsedCommentId > 0;

    const rootCommentsQuery = useInfiniteQuery<
        RootCommentsPage,
        Error,
        InfiniteData<RootCommentsPage, number | undefined>,
        ReturnType<typeof getRootCommentsQueryKey>,
        number | undefined
    >( {
        queryKey: getRootCommentsQueryKey( eventNo, normalizedCommentId ),
        initialPageParam: undefined,
        enabled: enabled && !!eventNo,
        queryFn: async ( { pageParam = undefined } ) => {
            // First page: use index API
            if ( pageParam === undefined ) {
                const [ listOk, listRes ] = await apiGetGuessCommentsIndex( {
                    eventNo,
                    size: COMMENTS_PAGE_SIZE,
                    childSize: CHILD_PAGE_SIZE,
                    commentId: shouldAttachCommentId ? parsedCommentId : undefined,
                } );
                const [ optionOk, optionRes ] = await apiListUserBoughtGuessOptions( { eventNo } );

                const listData = listOk ? listRes?.data ?? [] : [];
                const highlightServerIds = new Set<number>();
                if ( Array.isArray( listData ) ) {
                    ( listData as GuessCommentsIndexItem[] ).forEach( ( item ) => {
                        if ( get( item, "highlight", false ) ) {
                            highlightServerIds.add( item.id );
                        }
                        ( get( item, "children", [] ) as GuessCommentsWithoutTitleItem[] ).forEach( ( child ) => {
                            if ( get( child, "highlight", false ) ) {
                                highlightServerIds.add( child.id );
                            }
                        } );
                    } );
                }

                const nextComments = Array.isArray( listData )
                    ? ( listData as GuessCommentsIndexItem[] ).map( mapIndexItem )
                    : [];
                const filteredComments = highlightServerIds.size > 0
                    ? nextComments.filter( ( comment ) =>
                        highlightServerIds.has( comment.serverId ) ? Boolean( comment.highlight ) : true
                    ).map( ( comment ) => ( {
                        ...comment,
                        replies: ( comment.replies ?? [] ).filter( ( reply ) =>
                            highlightServerIds.has( reply.serverId ) ? Boolean( reply.highlight ) : true
                        ),
                    } ) )
                    : nextComments;

                const enrichedComments = enrichCommentsWithReplyMentions( filteredComments );
                const replyPagination: Record<number, { offsetId: number | null; hasMore: boolean }> = {};

                const sourceMap = new Map<number, GuessCommentsIndexItem>();
                if ( Array.isArray( listData ) ) {
                    ( listData as GuessCommentsIndexItem[] ).forEach( ( item ) => {
                        sourceMap.set( item.id, item );
                    } );
                }

                enrichedComments.forEach( ( comment ) => {
                    const source = sourceMap.get( comment.serverId );
                    const totalChild = source?.comments ?? comment.replies?.length ?? 0;
                    const loadedChildren = comment.replies?.length ?? 0;
                    replyPagination[comment.serverId] = {
                        offsetId: comment.replies && comment.replies.length > 0
                            ? comment.replies[comment.replies.length - 1].serverId
                            : null,
                        hasMore: totalChild > loadedChildren,
                    };
                } );

                if ( optionOk && optionRes?.data ) {
                    const setUserGuessOptions = useCommentsStore.getState().setUserGuessOptions;
                    setUserGuessOptions( optionRes.data );
                }

                const lastSource = Array.isArray( listData )
                    ? ( listData as GuessCommentsIndexItem[] ).at( -1 )
                    : undefined;
                const nextOffsetId = ( listData?.length ?? 0 ) >= COMMENTS_PAGE_SIZE && lastSource
                    ? lastSource.id
                    : undefined;

                return { records: enrichedComments, nextOffsetId, replyPagination };
            } else {
                // Subsequent pages: use page API
                const [ isOk, res ] = await apiGetGuessCommentsPage( {
                    eventNo,
                    offsetId: pageParam,
                    rootId: 0,
                    size: COMMENTS_PAGE_SIZE,
                } );
                const data = isOk ? res?.data ?? [] : [];
                if ( !Array.isArray( data ) || data.length === 0 ) {
                    return { records: [], nextOffsetId: undefined, replyPagination: {} };
                }
                const mapped = ( data as GuessCommentsPageItem[] ).map( mapPageRootItem );
                const enrichedMapped = enrichCommentsWithReplyMentions( mapped );
                const replyPagination: Record<number, { offsetId: number | null; hasMore: boolean }> = {};
                enrichedMapped.forEach( ( comment ) => {
                    replyPagination[comment.serverId] = {
                        offsetId: comment.replies && comment.replies.length > 0
                            ? comment.replies[comment.replies.length - 1].serverId
                            : null,
                        hasMore: false,
                    };
                } );

                const lastComment = enrichedMapped[enrichedMapped.length - 1];
                const nextOffsetId = data.length >= COMMENTS_PAGE_SIZE && lastComment
                    ? lastComment.serverId
                    : undefined;

                return { records: enrichedMapped, nextOffsetId, replyPagination };
            }
        },
        getNextPageParam: ( lastPage ) => lastPage.nextOffsetId,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
    } );

    // Update store's replyPagination and eventNo when query data changes
    useEffect( () => {
        if ( eventNo ) {
            useCommentsStore.setState( { eventNo, commentId: normalizedCommentId } );
        }
        if ( rootCommentsQuery.data?.pages ) {
            const allPagination: Record<number, { offsetId: number | null; hasMore: boolean }> = {};
            rootCommentsQuery.data.pages.forEach( ( page ) => {
                Object.assign( allPagination, page.replyPagination );
            } );
            useCommentsStore.setState( { replyPagination: allPagination } );
        }
    }, [ rootCommentsQuery.data, eventNo ] );

    const comments = useMemo( () => {
        const pages = rootCommentsQuery.data?.pages ?? [];
        return pages.flatMap( ( page ) => page.records );
    }, [ rootCommentsQuery.data ] );

    return { ...rootCommentsQuery, comments };
};

export const useCommentsTab = ( eventNo: string, commentId?: string ) => {
    const rootCommentsQuery = useRootCommentsQuery( eventNo, true, commentId );
    const replyDialogOpen = useCommentsStore( ( state ) => state.replyDialogOpen );
    const targetComment = useCommentsStore( ( state ) => state.targetComment );
    const submitReply = useCommentsStore( ( state ) => state.submitReply );
    const closeReplyDialog = useCommentsStore( ( state ) => state.closeReplyDialog );
    const replying = useCommentsStore( ( state ) => state.replying );
    const commentIdFromStore = useCommentsStore( ( state ) => state.commentId );
    const queryClient = useQueryClient();

    // Get comments from query
    const comments = rootCommentsQuery.comments;
    const loading = rootCommentsQuery.isLoading;
    const initialized = rootCommentsQuery.isSuccess || rootCommentsQuery.isError;

    const handleReplySubmit = useCallback( async ( content: string ) => {
        if ( !targetComment ) {
            return;
        }
        await submitReply( content );
        closeReplyDialog();
        // Only refetch the first page where new replies typically appear
        const queryKey = getRootCommentsQueryKey( eventNo, commentIdFromStore );
        queryClient.setQueryData<InfiniteData<RootCommentsPage, number | undefined>>( queryKey, ( oldData ) => {
            if ( !oldData ) {
                return oldData;
            }
            if ( oldData.pages.length <= 1 ) {
                return oldData;
            }
            return { ...oldData, pages: oldData.pages.slice( 0, 1 ), pageParams: oldData.pageParams.slice( 0, 1 ) };
        } );
        await queryClient.refetchQueries( { queryKey } );
    }, [ closeReplyDialog, commentIdFromStore, eventNo, queryClient, submitReply, targetComment ] );

    const handleDialogOpenChange = useCallback( ( open: boolean ) => {
        if ( !open ) {
            closeReplyDialog();
        }
    }, [ closeReplyDialog ] );

    return {
        comments,
        loading,
        initialized,
        rootCommentsQuery,
        replyDialogOpen,
        targetComment,
        handleReplySubmit,
        handleDialogOpenChange,
        replying,
    };
};

type ToggleLikeVariables = { serverId: number; shouldLike: boolean };

const updateCommentsByServerId = (
    comments: CommentData[],
    serverId: number,
    updater: ( comment: CommentData ) => CommentData,
): CommentData[] => {
    let modified = false;
    const next = comments.map( ( comment ) => {
        if ( comment.serverId === serverId ) {
            modified = true;
            return updater( comment );
        }
        if ( comment.replies && comment.replies.length > 0 ) {
            const replies = updateCommentsByServerId( comment.replies, serverId, updater );
            if ( replies !== comment.replies ) {
                modified = true;
                return { ...comment, replies };
            }
        }
        return comment;
    } );
    return modified ? next : comments;
};

const updateRootPages = (
    data: InfiniteData<RootCommentsPage, number | undefined> | undefined,
    serverId: number,
    updater: ( comment: CommentData ) => CommentData,
) => {
    if ( !data ) return data;
    let changed = false;
    const pages = data.pages.map( ( page ) => {
        const records = updateCommentsByServerId( page.records, serverId, updater );
        if ( records !== page.records ) {
            changed = true;
            return { ...page, records };
        }
        return page;
    } );
    if ( !changed ) {
        return data;
    }
    return { ...data, pages };
};

const mergeReplies = ( existing: CommentData[] | undefined, incoming: CommentData[] ) => {
    if ( !incoming || incoming.length === 0 ) {
        return existing ?? [];
    }
    const base = existing ?? [];
    const existingIds = new Set( base.map( ( reply ) => reply.serverId ) );
    const filtered = incoming.filter( ( reply ) => !existingIds.has( reply.serverId ) );
    if ( filtered.length === 0 ) {
        return base;
    }
    return [ ...base, ...filtered ];
};

const appendRepliesToRoot = (
    data: InfiniteData<RootCommentsPage, number | undefined> | undefined,
    rootServerId: number,
    newReplies: CommentData[],
    nextOffsetId: number | null,
    hasMore: boolean,
) => {
    if ( !data ) {
        return data;
    }
    let changed = false;
    const pages = data.pages.map( ( page ) => {
        const hasRootComment = page.records.some( ( comment ) => comment.serverId === rootServerId );
        const existingPagination = page.replyPagination[rootServerId];
        if ( !hasRootComment && !existingPagination ) {
            return page;
        }

        let pageChanged = false;
        let records = page.records;
        if ( hasRootComment && newReplies.length > 0 ) {
            let repliesChanged = false;
            const nextRecords = page.records.map( ( comment ) => {
                if ( comment.serverId !== rootServerId ) {
                    return comment;
                }
                const mergedReplies = mergeReplies( comment.replies, newReplies );
                if ( mergedReplies === comment.replies ) {
                    return comment;
                }
                repliesChanged = true;
                return { ...comment, replies: mergedReplies };
            } );
            if ( repliesChanged ) {
                records = nextRecords;
                pageChanged = true;
            }
        }

        let replyPagination = page.replyPagination;
        if ( existingPagination || hasRootComment ) {
            const nextMeta = { offsetId: nextOffsetId, hasMore };
            const currentMeta = existingPagination ?? nextMeta;
            const shouldUpdate = currentMeta.offsetId !== nextMeta.offsetId || currentMeta.hasMore !== nextMeta.hasMore
                || !existingPagination;
            if ( shouldUpdate ) {
                replyPagination = { ...page.replyPagination, [rootServerId]: nextMeta };
                pageChanged = true;
            }
        }

        if ( pageChanged ) {
            changed = true;
            return { ...page, records, replyPagination };
        }
        return page;
    } );
    return changed ? { ...data, pages } : data;
};

const getServerIdFromLocalId = ( id: string | number ): number => {
    if ( typeof id === "number" ) {
        return id;
    }
    const numeric = parseInt( id.replace( /[^0-9]/g, "" ), 10 );
    if ( Number.isFinite( numeric ) ) {
        return numeric;
    }
    const fallback = Number( id );
    return Number.isFinite( fallback ) ? fallback : 0;
};

export const useToggleCommentLike = ( eventNo?: string ) => {
    const queryClient = useQueryClient();
    const commentIdFromStore = useCommentsStore( ( state ) => state.commentId );

    return useMutation( {
        mutationFn: async ( variables: ToggleLikeVariables ) => {
            const requester = variables.shouldLike ? apiLikeGuessComment : apiUnlikeGuessComment;
            const [ isOk ] = await requester( { id: variables.serverId } );
            if ( !isOk ) {
                throw new Error( "Failed to toggle like" );
            }
            return variables;
        },
        onMutate: async ( variables ) => {
            if ( !eventNo ) return {};
            await queryClient.cancelQueries( { queryKey: getRootCommentsQueryKey( eventNo, commentIdFromStore ) } );
            const previousData = queryClient.getQueryData<InfiniteData<RootCommentsPage, number | undefined>>( [
                "rootComments",
                eventNo,
                commentIdFromStore,
            ] );
            const delta = variables.shouldLike ? 1 : -1;
            const updated = updateRootPages(
                previousData,
                variables.serverId,
                ( comment ) => ( {
                    ...comment,
                    likes: Math.max( 0, comment.likes + delta ),
                    likeTag: variables.shouldLike,
                } ),
            );
            if ( updated ) {
                queryClient.setQueryData( getRootCommentsQueryKey( eventNo, commentIdFromStore ), updated );
            }
            return { previousData };
        },
        onError: ( _error, _vars, context ) => {
            if ( !eventNo ) return;
            if ( context?.previousData ) {
                queryClient.setQueryData(
                    getRootCommentsQueryKey( eventNo, commentIdFromStore ),
                    context.previousData,
                );
            }
        },
        // Removed onSettled invalidateQueries to avoid refetching all pages from the beginning
        // The optimistic update in onMutate is sufficient, and we only need to rollback on error
    } );
};

export const useLoadMoreReplies = ( eventNo?: string ) => {
    const queryClient = useQueryClient();
    const replyPagination = useCommentsStore( ( state ) => state.replyPagination );
    const commentIdFromStore = useCommentsStore( ( state ) => state.commentId );

    const loadMoreReplies = useCallback( async ( rootId: string ) => {
        if ( !eventNo ) {
            return;
        }
        const rootServerId = getServerIdFromLocalId( rootId );
        if ( !rootServerId ) {
            return;
        }
        const pagination = replyPagination[rootServerId] ?? { offsetId: null, hasMore: true };
        if ( !pagination.hasMore ) {
            return;
        }
        try {
            const [ isOk, res ] = await apiGetGuessCommentsPage( {
                eventNo,
                rootId: rootServerId,
                offsetId: pagination.offsetId ?? undefined,
                size: LOAD_MORE_SIZE,
            } );
            const list = Array.isArray( res?.data ) && isOk ? ( res?.data as GuessCommentsWithoutTitleItem[] ) : [];
            const currentData = queryClient.getQueryData<InfiniteData<RootCommentsPage, number | undefined>>( [
                "rootComments",
                eventNo,
                commentIdFromStore,
            ] );
            if ( !currentData ) {
                return;
            }

            let enrichedReplies: CommentData[] = [];
            if ( list.length > 0 ) {
                const replies = list.map( ( child ) =>
                    toCommentData( child, { rootId: rootServerId, parentId: rootServerId } )
                );
                const existingComments = currentData.pages.flatMap( ( page ) => page.records );
                const idMap = buildCommentUserMap( existingComments );
                enrichedReplies = enrichCommentsWithReplyMentions( replies, { idToNameMap: idMap } );
            }

            const nextOffsetId = enrichedReplies.length > 0
                ? enrichedReplies[enrichedReplies.length - 1].serverId
                : pagination.offsetId ?? null;
            const hasMoreNext = list.length > 0 ? list.length >= LOAD_MORE_SIZE : false;

            const updatedPages = appendRepliesToRoot(
                currentData,
                rootServerId,
                enrichedReplies,
                nextOffsetId,
                hasMoreNext,
            );
            if ( updatedPages && updatedPages !== currentData ) {
                queryClient.setQueryData( getRootCommentsQueryKey( eventNo, commentIdFromStore ), updatedPages );
            }
        } catch ( error ) {
            console.error( "[comments] Failed to load replies", error );
        }
    }, [ commentIdFromStore, eventNo, queryClient, replyPagination ] );

    return { loadMoreReplies };
};

export const useCommentItem = ( comment: CommentData, replyParentId: string, onLike?: () => void ) => {
    const [ isExpanded, setIsExpanded ] = useState( false );
    const selectedCommentId = useCommentsStore( ( state ) => state.selectedCommentId );
    const toggleSelect = useCommentsStore( ( state ) => state.toggleSelect );
    const openReplyDialog = useCommentsStore( ( state ) => state.openReplyDialog );
    const eventNoFromStore = useCommentsStore( ( state ) => state.eventNo );
    const toggleLike = useToggleCommentLike( eventNoFromStore || comment.eventNo );

    const isSelected = selectedCommentId === comment.id;
    const isLongContent = comment.content.length > 150;
    const displayContent = useMemo( () => {
        if ( !isLongContent || isExpanded ) {
            return comment.content;
        }
        return `${comment.content.slice( 0, 150 )}...`;
    }, [ comment.content, isExpanded, isLongContent ] );
    const isLiked = Boolean( comment.likeTag );

    const handleCommentClick = useCallback( () => {
        toggleSelect( comment.id );
    }, [ comment.id, toggleSelect ] );

    const handleReplyClick = useCallback( () => {
        if ( !isLogin() ) {
            openLoginModal( "auth-guard" );
            return;
        }

        openReplyDialog( comment, replyParentId );
    }, [ comment, openReplyDialog, replyParentId ] );

    const handleLike = useCallback( () => {
        if ( !isLogin() ) {
            openLoginModal( "auth-guard" );
            return;
        }

        if ( onLike ) {
            onLike();
        } else {
            if ( !comment.serverId ) return;
            toggleLike.mutate( { serverId: comment.serverId, shouldLike: !comment.likeTag } );
        }
    }, [ onLike, comment.likeTag, comment.serverId, toggleLike ] );

    const toggleExpanded = useCallback( () => {
        setIsExpanded( ( prev ) => !prev );
    }, [] );

    return {
        isSelected,
        isLongContent,
        displayContent,
        isExpanded,
        isLiked,
        handleCommentClick,
        handleReplyClick,
        handleLike,
        toggleExpanded,
    };
};

export const useCommentInput = ( eventNo: string ) => {
    const [ content, setContent ] = useState( "" );
    const submitComment = useCommentsStore( ( state ) => state.submitComment );
    const isPosting = useCommentsStore( ( state ) => state.postingComment );
    const userGuessOptions = useCommentsStore( ( state ) => state.userGuessOptions );
    const commentIdFromStore = useCommentsStore( ( state ) => state.commentId );
    const [ selectedGuessOption, setSelectedGuessOption ] = useState<UserBoughtEventGuessItem | null>( null );
    const queryClient = useQueryClient();
    const maxLength = 800;

    const remainingChars = maxLength - content.length;

    useEffect( () => {
        if ( !userGuessOptions || userGuessOptions.length === 0 ) {
            setSelectedGuessOption( null );
            return;
        }
        if ( userGuessOptions.length === 1 ) {
            setSelectedGuessOption( userGuessOptions[0] );
            return;
        }
        setSelectedGuessOption( ( prev ) => {
            if (
                prev && userGuessOptions.some( ( option ) =>
                    option.guessNo === prev.guessNo && option.betType === prev.betType
                )
            ) {
                return prev;
            }
            return userGuessOptions[0];
        } );
    }, [ userGuessOptions ] );

    const handleChange = useCallback( ( value: string ) => {
        // Filter out Chinese characters
        const filteredValue = value.replace( /[\u4e00-\u9fff]/g, "" );
        setContent( filteredValue.slice( 0, maxLength ) );
    }, [ maxLength ] );

    const handleSelectGuessOption = useCallback( ( option: UserBoughtEventGuessItem ) => {
        setSelectedGuessOption( option );
    }, [] );

    const handlePost = useCallback( async () => {
        if ( !isLogin() ) {
            openLoginModal( "auth-guard" );
            return;
        }

        const trimmed = content.trim();
        if ( !trimmed ) {
            return;
        }
        await submitComment( trimmed, selectedGuessOption );
        setContent( "" );
        // Only refetch the first page where new comments typically appear
        const queryKey = getRootCommentsQueryKey( eventNo, commentIdFromStore );
        queryClient.setQueryData<InfiniteData<RootCommentsPage, number | undefined>>( queryKey, ( oldData ) => {
            if ( !oldData ) {
                return oldData;
            }
            if ( oldData.pages.length <= 1 ) {
                return oldData;
            }
            return { ...oldData, pages: oldData.pages.slice( 0, 1 ), pageParams: oldData.pageParams.slice( 0, 1 ) };
        } );
        await queryClient.refetchQueries( { queryKey } );
    }, [ commentIdFromStore, content, eventNo, queryClient, selectedGuessOption, submitComment ] );

    return {
        content,
        remainingChars,
        maxLength,
        handleChange,
        handlePost,
        isPosting,
        userGuessOptions,
        selectedGuessOption,
        handleSelectGuessOption,
        shouldShowGuessSelector: ( userGuessOptions?.length ?? 0 ) > 0,
    };
};

interface UseReplyDialogParams {
    targetComment: CommentData | null;
    onSubmit: ( content: string ) => Promise<void> | void;
    onClose: () => void;
}

export const useReplyDialog = ( { targetComment, onSubmit, onClose }: UseReplyDialogParams ) => {
    const [ replyContent, setReplyContent ] = useState( "" );
    const maxLength = 800;
    const textAreaRef = useRef<HTMLTextAreaElement | null>( null );
    const maxVisibleRows = 8;
    const lineHeightPx = 24;
    const maxHeightPx = maxVisibleRows * lineHeightPx;

    const adjustHeight = useCallback( () => {
        if ( textAreaRef.current ) {
            const textarea = textAreaRef.current;
            textarea.style.height = "auto";
            const nextHeight = Math.min( textarea.scrollHeight, maxHeightPx );
            textarea.style.height = `${nextHeight}px`;
            textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? "auto" : "hidden";
        }
    }, [ maxHeightPx ] );

    useEffect( () => {
        setReplyContent( "" );
        if ( textAreaRef.current ) {
            textAreaRef.current.style.height = "auto";
        }
    }, [ targetComment?.id ] );

    useEffect( () => {
        adjustHeight();
    }, [ replyContent, adjustHeight ] );

    const remainingChars = maxLength - replyContent.length;

    const handleSubmit = useCallback( async () => {
        const trimmed = replyContent.trim();
        if ( !trimmed || !targetComment ) {
            return;
        }
        await onSubmit( trimmed );
        setReplyContent( "" );
    }, [ onSubmit, replyContent, targetComment ] );

    const handleClose = useCallback( () => {
        setReplyContent( "" );
        onClose();
    }, [ onClose ] );

    const handleChange = useCallback( ( value: string ) => {
        // Filter out Chinese characters
        const filteredValue = value.replace( /[\u4e00-\u9fff]/g, "" );
        setReplyContent( filteredValue.slice( 0, maxLength ) );
    }, [ maxLength ] );

    return { replyContent, remainingChars, maxLength, textAreaRef, handleChange, handleSubmit, handleClose };
};
